// Unified branded mobile top header — FitAura icon + wordmark + page title +
// optional actions. Implements a smooth Play-Store style "shrinking header":
// instead of a binary scrolled toggle, we map scrollY [0..72px] to a continuous
// progress value [0..1] and interpolate size/padding/opacity per frame via
// requestAnimationFrame. Same component is used by every mobile page through
// MobileShell, so the behaviour is identical app-wide.
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { NotificationsBell } from "@/components/NotificationsBell";
import { UserTierBadge } from "@/components/UserTierBadge";

interface MobileHeaderProps {
  /** Section title rendered as a tracked uppercase label under the wordmark. */
  title: string;
  /** Optional right-aligned slot — typically a CreditsChip or icon button. */
  actions?: React.ReactNode;
  className?: string;
}

const SCROLL_RANGE = 72; // px over which the header fully collapses

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export const MobileHeader = ({ title, actions, className }: MobileHeaderProps) => {
  // progress: 0 = fully expanded, 1 = fully collapsed
  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const update = () => {
      rafRef.current = null;
      const y = Math.max(0, window.scrollY);
      const next = Math.min(1, y / SCROLL_RANGE);
      setProgress((prev) => (Math.abs(prev - next) < 0.005 ? prev : next));
    };
    const onScroll = () => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Interpolated values — keep transitions buttery by driving via style props.
  const iconSize = lerp(36, 28, progress); // 9 -> 7 in tailwind units (px)
  const wordmarkSize = lerp(15, 13, progress);
  const titleSize = lerp(10, 9, progress);
  const titleOpacity = lerp(1, 0.7, progress);
  const titleMarginTop = lerp(4, 2, progress);
  const padBottom = lerp(12, 8, progress);
  const iconGlow = lerp(0.55, 0.25, progress);

  return (
    <header
      className={cn(
        "sticky top-0 z-30 -mx-5 border-b border-white/5 bg-background/85 px-5 backdrop-blur-xl",
        "pt-[max(env(safe-area-inset-top),10px)]",
        className,
      )}
      style={{ paddingBottom: `${padBottom}px` }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="grid shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-primary/30 to-accent/20 ring-1 ring-primary/30"
            style={{
              width: `${iconSize}px`,
              height: `${iconSize}px`,
              boxShadow: `0 0 18px -4px hsl(var(--primary) / ${iconGlow})`,
            }}
          >
            <img
              src="/app-icon-128.png"
              alt="FitAura AI"
              width={36}
              height={36}
              className="h-full w-full object-cover"
            />
          </span>
          <div className="min-w-0">
            <span
              className="font-display block font-bold leading-none tracking-tight"
              style={{ fontSize: `${wordmarkSize}px` }}
            >
              FitAura<span className="text-primary"> AI</span>
            </span>
            <span
              className="font-display block truncate font-semibold uppercase tracking-[0.24em] text-muted-foreground"
              style={{
                fontSize: `${titleSize}px`,
                marginTop: `${titleMarginTop}px`,
                opacity: titleOpacity,
              }}
            >
              {title}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <UserTierBadge size="sm" />
          {actions}
          <NotificationsBell />
        </div>
      </div>
    </header>
  );
};
